import { Loader } from "../../../../components/core/Loader/Loader";
import {
  StyledAddCertificationModal,
  StyledAlertMessage,
} from "./CertificationModal.styled";
import {
  SAVE_LABEL,
  CANCEL_LABEL,
  ADD_NEW_CERTIFICATION,
  DETAILS_LABEL,
  TARGETS_LABEL,
  TITLE_LABEL,
  CATEGORY_LABEL,
  EXECUTION_MODE_LABEL,
  URL_LABEL,
  DESCRIPTION_LABEL,
  IS_REQUIRED_LABEL,
  EDIT_CERTIFICATION_LABEL,
  VALIDITY_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  CHANGE_DESTINATION_ALERT_MESSAGE,
  IS_NOT_VALID_URL_LABEL,
  COLLABORATOR_LABEL,
} from "../../../../constants/Dictionary";
import { Modal } from "../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../components/core/Button/Button";
import { ReactComponent as CancelIcon } from "../../../../icons/close.svg";
import { ReactComponent as SaveIcon } from "../../../../icons/save.svg";
import { ReactComponent as OpenBookIcon } from "../../../../icons/bookOpen.svg";
import { ReactComponent as RecipientIcon } from "../../../../icons/userCircle.svg";
import {
  Certification,
  CertificationRequest,
} from "../../../../types/api/certifications.type";
import { useEffect, useMemo } from "react";
import { FontWeightEnum, IconSizeEnum } from "../../../../types/AppTypes";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, Resolver, useForm } from "react-hook-form";
import { FormSection } from "../../../../components/FormSection/FormSection";
import { FormField } from "../../../../components/core/FormField/FormField";
import TextInput from "../../../../components/core/TextInput/TextInput";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../components/core/Select/Select";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import { Divider } from "../../../../components/core/Divider/Divider";
import { useAppSelector } from "../../../../hooks/store.hook";
import {
  CHARACTER_MAX_LENGTH,
  TimestampLabelEnum,
} from "../../../../constants/AppConstants";
import TextArea from "../../../../components/core/TextArea/TextArea";
import { BodyTyp } from "../../../../components/Typography/Typography";
import { theme } from "../../../../constants/StyledComponents";
import { ReactComponent as WarningIcon } from "../../../../icons/warning.svg";
import { SelectTimespan } from "../../../../components/SelectTimespan/SelectTimespan";
import { OrgAccordion } from "../../../../components/OrgAccordion/OrgAccordion";
import { Checkbox } from "../../../../components/core/Checkbox/Checkbox";
import { useUpdateFormationCertification } from "../../../../hooks/formationCertification.hook";
import { useFormationCollaborators } from "../../../../hooks/formationCollaborators.hook";

type FormData = {
  name: string;
  validityPeriod?: string | null;
  category: string | null;
  url?: string;
  executionMode: string | null;
  description: string;
  internalGrade?: string[] | null;
  cc?: string[];
  adHocCollaborator: boolean;
  collaboratorId?: number | null;
};

type CertificationModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  record?: Certification;
};

export const CertificationModal = ({
  isOpen,
  closeModal,
  record,
}: CertificationModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);
  const {
    mutateAsync: updateCertification,
    isPending: isUpdatingCertification,
  } = useUpdateFormationCertification();

  const schema = yup
    .object({
      name: yup.string().required(`${TITLE_LABEL} ${IS_REQUIRED_LABEL}`),
      validityPeriod: yup.string().nullable(),
      category: yup
        .string()
        .nullable()
        .required(`${CATEGORY_LABEL} ${IS_REQUIRED_LABEL}`),
      url: yup.string().url(`${URL_LABEL} ${IS_NOT_VALID_URL_LABEL}`),
      executionMode: yup
        .string()
        .nullable()
        .required(`${EXECUTION_MODE_LABEL} ${IS_REQUIRED_LABEL}`),
      description: yup
        .string()
        .required(`${DESCRIPTION_LABEL} ${IS_REQUIRED_LABEL}`),
      internalGrade: yup.array(yup.string().required()).nullable(),
      cc: yup.array().of(yup.string().required()),
      collaboratorId: yup
        .number()
        .nullable()
        .when(["adHocCollaborator"], {
          is: true,
          then: (field) =>
            field.required(`${COLLABORATOR_LABEL} ${IS_REQUIRED_LABEL}`),
        }),
      adHocCollaborator: yup.boolean().required(),
    })
    .required();

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  const onSubmit = async (data: FormData) => {
    const body: CertificationRequest = {
      name: data.name,
      validityPeriod: data.validityPeriod ?? null,
      category: data.category!,
      executionMode: data.executionMode!,
      url: data.url ? data.url : null,
      description: data.description,
      internalGrade: data.internalGrade ?? null,
      cc: data.cc ?? [],
      adHocCollaborator: data.adHocCollaborator,
      collaboratorId: data.collaboratorId ?? null,
    };

    await updateCertification({ body, record });

    closeModal();
  };

  useEffect(() => {
    if (record) {
      reset({
        name: record.name,
        ...(record.validityPeriod && { validityPeriod: record.validityPeriod }),
        category: record.category,
        url: record.url ?? undefined,
        executionMode: record.executionMode,
        description: record.description,
        ...(record.internalGrade && { internalGrade: record.internalGrade }),
        cc: record.cc,
        adHocCollaborator: record.adHocCollaborator,
        ...(record.collaborator && { collaboratorId: record.collaborator.id }),
      });
    } else {
      reset({
        name: "",
        validityPeriod: null,
        category: null,
        url: "",
        executionMode: null,
        description: "",
        adHocCollaborator: false,
        cc: [],
        internalGrade: null,
        collaboratorId: null,
      });
    }
  }, [record]);

  const selectedAdPersonam = watch("adHocCollaborator");

  const resetTargets = () => {
    setValue("cc", []);
    setValue("internalGrade", null);
    setValue("collaboratorId", null);
  };

  const onClose = () => {
    closeModal();
  };

  const targetsAreChanged =
    record &&
    (dirtyFields.adHocCollaborator ||
      dirtyFields.collaboratorId ||
      dirtyFields.internalGrade ||
      dirtyFields.cc);

  const defaultCollaborator =
    record && record.collaborator
      ? {
          value: record.collaborator.id,
          label: `${record.collaborator.surname} ${record.collaborator.name}`,
        }
      : null;

  const {
    data: collaboratorData,
    initLoading: loadingCollaborators,
    canLoadMore: hasMore,
    search: searchValue,
    setSearch: setSearchValue,
    fetchMore,
    fetchingMore,
  } = useFormationCollaborators({
    queryKey: "certification-modal-collaborators",
  });

  const collaborators = useMemo<SelectOption<number>[]>(() => {
    const options = collaboratorData.map((c) => ({
      value: c.id,
      label: `${c.surname} ${c.name}`,
    }));
    if (
      defaultCollaborator &&
      !options.find((o) => o.value === defaultCollaborator.value)
    ) {
      return [defaultCollaborator, ...options];
    }
    return options;
  }, [collaboratorData, defaultCollaborator]);

  return (
    <Modal
      label={record ? EDIT_CERTIFICATION_LABEL : ADD_NEW_CERTIFICATION}
      isOpen={isOpen}
      {...(targetsAreChanged && {
        message: (
          <StyledAlertMessage>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
              <WarningIcon />
            </IconManager>
            <BodyTyp
              color={theme.colors.red_1}
              fontWeight={FontWeightEnum.SEMIBOLD}
            >
              {CHANGE_DESTINATION_ALERT_MESSAGE}
            </BodyTyp>
          </StyledAlertMessage>
        ),
      })}
      rightButton={
        <Button
          icon={<SaveIcon />}
          disabled={Object.keys(dirtyFields).length === 0}
          label={SAVE_LABEL}
          type="submit"
          form="certificationForm"
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={onClose}
        />
      }
    >
      <StyledAddCertificationModal>
        <Loader loading={isUpdatingCertification}>
          <form id="certificationForm" onSubmit={handleSubmit(onSubmit)}>
            <FormSection label={DETAILS_LABEL} icon={<OpenBookIcon />}>
              <div className="form-row">
                <FormField error={errors.name?.message}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={TITLE_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.validityPeriod?.message}>
                  <Controller
                    name="validityPeriod"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <SelectTimespan
                        label={VALIDITY_LABEL}
                        value={value ?? undefined}
                        onChange={(v) => onChange(v ?? null)}
                        timestampLabels={[
                          TimestampLabelEnum.month,
                          TimestampLabelEnum.year,
                        ]}
                      />
                    )}
                  />
                </FormField>

                <FormField error={errors.category?.message}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={CATEGORY_LABEL}
                        options={orgSettings.certifications.categories}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className="form-row">
                <FormField error={errors.executionMode?.message}>
                  <Controller
                    name="executionMode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={EXECUTION_MODE_LABEL}
                        options={orgSettings.certifications.executionModes}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.url?.message}>
                  <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                      <TextInput label={URL_LABEL} maxLength={255} {...field} />
                    )}
                  />
                </FormField>
              </div>
              <div>
                <FormField error={errors.description?.message}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label={DESCRIPTION_LABEL}
                        maxLength={CHARACTER_MAX_LENGTH}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
            <Divider />

            <FormSection
              label={TARGETS_LABEL}
              icon={<RecipientIcon />}
              rightContent={
                <Controller
                  name="adHocCollaborator"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      label={"Ad Personam"}
                      value={value}
                      onChange={(checked) => {
                        resetTargets();
                        onChange(checked);
                      }}
                    />
                  )}
                />
              }
            >
              <div
                className={`form-row ${!selectedAdPersonam ? "visible" : "hidden"}`}
              >
                <FormField error={errors.internalGrade?.message}>
                  <Controller
                    name="internalGrade"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        variant={SelectVariantEnum.MULTI_SELECT}
                        label={INTERNAL_GRADE_IT_LABEL}
                        options={orgSettings.collaborator.internalGrades}
                        value={value ?? undefined}
                        onChange={(value) => {
                          onChange(value && value.length > 0 ? value : null);
                        }}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className={`${!selectedAdPersonam ? "visible" : "hidden"}`}>
                <Controller
                  name="cc"
                  control={control}
                  render={({ field }) => <OrgAccordion {...field} />}
                />
              </div>
              <div
                className={`form-row space ${selectedAdPersonam ? "visible" : "hidden"}`}
              >
                <FormField error={errors.collaboratorId?.message}>
                  <Controller
                    name="collaboratorId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.DEFAULT}
                        label={COLLABORATOR_LABEL}
                        options={collaborators}
                        onSearch={setSearchValue}
                        searchValue={searchValue}
                        loading={loadingCollaborators}
                        canLoadMore={hasMore}
                        fetchMore={fetchMore}
                        fetchingMore={fetchingMore}
                        withSearch
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
          </form>
        </Loader>
      </StyledAddCertificationModal>
    </Modal>
  );
};
